import React, { lazy } from "react";
import { SwiperOptions } from "swiper/types";
import { TemplateType } from "./templateConfigs";

export enum ComponentType {
  Header = "header",
  WinnersDreamCar = "winners_dream_car",
  SkillToLuck = "skill_to_luck",
  ExtraPrizes = "extraPrizes",
  ExtraPrizesHeader = "extra_prizes_header",
  ExtraPrizesWrapper = "extra_prizes_wrapper",
  ExtraPrizesRunnersUp = "runnersUp",
  ExtraPrizesClosestByCar = "closestByCar",
  FeelingLucky = "feeling_lucky",
  FeaturedFeelingLucky = "featured_feeling_lucky",
  FeelingLuckyTitle = "feeling_lucky_title",
  FeelingLuckyDailySpin = "feeling_lucky_daily_spin",
  WinnersAre = "winners_are",
  GamePhoto = "gamePhoto",
  WinnersGamePhoto = "winners_game_photo",
  WinnersAreTitle = "winners_are_title",
  WinnersNearMisses = "winners_near_misses",
  WinnersRewardScheme = "winners_reward_scheme",
  Judging = "judging",
  JudgingTitle = "judging_title",
  JudgingMain = "judging_main",
  JudgingSideInformation = "judging_side_information",
  Chance = "chance",
  Judges = "judges",
  ExploreCompetitions = "explore_competitions",
  WinnerIs = "winner_is",
  HouseSlider = "house_slider",
  AllWinnersPrizes = "all_winners_prizes",
  ResultsDate = "results_date",
  PrizeWon = "prize_won",
  TheStory = "the_story",
  PickOfTheWeekList = "pick_of_the_week_list",
  TopWinners = "top_winners",
  PrizeDetails = "prizeDetails",
  PrizeMedia = "prizeMedia",
  SectionTitle = "section_title",
  Quote = "quote",
  Video = "video",
  CompetitionCard = "competition_card",
  AsideTitle = "aside_title",
  AsideCompetition = "aside_competition",
  MultipleWinners = "multiple_winners",
}

export type ComponentGeneric<T extends ComponentType> = {
  id?: string;
  type: T;
};

export type WinnersHeaderComponent = ComponentGeneric<ComponentType.Header> & {
  data: {
    title: string;
    subtitle: string;
    assets: {
      type: "image" | "video";
      mediaUrl: string;
      mediaThumbnailUrl: string;
    };
    ticketPriceWon: string;
    widgetCompetitionId: number | null;
  };
};
export type SectionTitleComponent = ComponentGeneric<ComponentType.SectionTitle> & {
  title: string;
};

export type QuoteComponent = ComponentGeneric<ComponentType.Quote> & {
  quote: string;
  author?: string;
};

export type VideoComponent = ComponentGeneric<ComponentType.Video> & {
  assets: {
    type: "image" | "video";
    mediaUrl: string;
    mediaThumbnailUrl: string;
  };
  textDesc?: string;
  prizeText?: string;
  className?: string;
};

export type CompetitionCardComponent = ComponentGeneric<ComponentType.CompetitionCard> & {
  widgetCompetitionId: number | null;
  ticketPriceWon: string;
  title?: string;
  description?: string;
};

export type PrizeDetailsComponent = ComponentGeneric<ComponentType.PrizeDetails> & {
  data: {
    competitionName: string;
    feelingLuckyPrizeId: number;
    picksOfTheWeekCompetitionIds: number[];
    prizeName: string;
    prizeUrl: string;
    resultDate: string;
    story: string;
    storyPreview: string;
    competitionId?: number;
    instantWinWinnersUrl?: string;
    multipleWinners?: MultipleWinners[];
  };
};

export type MultipleWinnersComponent = ComponentGeneric<ComponentType.MultipleWinners> & {
  multipleWinners: MultipleWinners[];
};

export type MultipleWinners = {
  winnerName: string;
  prizeName: string;
  prizeImageUrl: string;
};

export type ResultsDateComponent = ComponentGeneric<ComponentType.ResultsDate> & {
  title: string;
  date: string;
  competitionId: number;
};

export type PrizeWonComponent = ComponentGeneric<ComponentType.PrizeWon> & {
  title: string;
  subtitle: string;
  prizeName: string;
  prizeUrl: string;
  buttonText: string;
};

export type TheStoryComponent = ComponentGeneric<ComponentType.TheStory> & {
  [key: string]: any;
};

export type AsideTitleComponent = ComponentGeneric<ComponentType.AsideTitle> & {
  title: string;
};

export type FeelingLuckyTitleComponent = ComponentGeneric<ComponentType.FeelingLuckyTitle> & {
  title: string;
};

export type AsideCompetitionComponent = ComponentGeneric<ComponentType.AsideCompetition> & {
  competitionId: number;
  subtitle: string;
};

export type ExtraPrizesWrapperComponent = ComponentGeneric<ComponentType.ExtraPrizesWrapper>;

// Player type for both RunnersUp and ClosestByCar
export type ExtraPrizePlayer = {
  name: string;
  firstName: string;
  surname: string;
  prizeName: string;
  make: string;
  model: string;
  countryCode: string;
  prizeThumbnailUrl: string;
};

// Data structure for RunnersUp
export type ExtraPrizesRunnersUpData = {
  numberOfPlayers: number;
  currencySymbol: string;
  prizeValue: number;
  players: ExtraPrizePlayer[];
};

// Data structure for ClosestByCar
export type ExtraPrizesClosestByCarData = {
  numberOfPlayers: number;
  currencySymbol: string;
  prizeValue: number;
  players: ExtraPrizePlayer[];
};

export type ExtraPrizesComponent = ComponentGeneric<ComponentType.ExtraPrizes> & {
  data: Array<
    | {
      type: ComponentType.ExtraPrizesRunnersUp;
      data: ExtraPrizesRunnersUpData;
    }
    | {
      type: ComponentType.ExtraPrizesClosestByCar;
      data: ExtraPrizesClosestByCarData;
    }
  >;
};

export type ExtraPrizesHeaderComponent = ComponentGeneric<ComponentType.ExtraPrizesHeader> & {
  players: number;
  prize: number;
};

export type ExtraPrizesRunnersUpComponent = ComponentGeneric<ComponentType.ExtraPrizesRunnersUp> & {
  data: ExtraPrizesRunnersUpData;
};

export type ExtraPrizesClosestByCarComponent = ComponentGeneric<ComponentType.ExtraPrizesClosestByCar> & {
  data: ExtraPrizesClosestByCarData;
};
export type FeaturedFeelingLuckyComponent = ComponentGeneric<ComponentType.FeaturedFeelingLucky> & {
  feelingLuckyPrizeId: number;
  quickAddTitle?: string;
};
export type SmarticoDailySpinComponent = ComponentGeneric<ComponentType.FeelingLuckyDailySpin> & {
  badgeText?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
};

export type GamePhotoComponent = ComponentGeneric<ComponentType.GamePhoto> & {
  data: {
    competitionPictureGuid: string;
    gamePhotoUrl: string;
    hasFiveZoneRadius: boolean;
    judgedPhotoUrl: string;
    judgedPosition: [number, number];
    userEntriesPhotoUrl: string | null;
    userClosestPhotoUrl: string | null;
    userClosestPosition: [number, number] | null;
    winnerPhotoUrl: string;
    winnerPosition: [number, number];
    zone1OverrideValue: null;
    zone2OverrideValue: null;
    zone3OverrideValue: null;
    zone4OverrideValue: null;
    zone5OverrideValue: null;
    zonePhotoUrl: string;
    gameCreditAwarded: string;
    totalGameCredit: string;
  };
};

export type WinnersAreTitleComponent = ComponentGeneric<ComponentType.WinnersAreTitle> & {
  title: string;
};

export type WinnersRewardSchemeComponent = ComponentGeneric<ComponentType.WinnersRewardScheme> & {
  data: GamePhotoComponent["data"] & {
    zones?: {
      zone: string;
      percentage: number;
    }[];
  };
};

export type WinnersNearMissesComponent = ComponentGeneric<ComponentType.WinnersNearMisses> & {
  data: GamePhotoComponent["data"];
};

export type WinnersGamePhotoComponent = ComponentGeneric<ComponentType.WinnersGamePhoto> & {
  data: GamePhotoComponent["data"];
};

export type ChanceComponent = ComponentGeneric<ComponentType.Chance> & {
  data: {
    widgetCompetitionId: number;
  };
};

export type JudgingTitleComponent = ComponentGeneric<ComponentType.JudgingTitle> & {
  title: string;
};

export type JudgingSideInformationComponent = ComponentGeneric<ComponentType.JudgingSideInformation> & {
  data: JudgingComponent["data"] & {
    description: string;
    disableJudgingVideo?: boolean;
    disableMeetTheJudges?: boolean;
    disableJudgingFAQs?: boolean;
    disableConfirmationLetter?: boolean;
    title?: string;
    template: TemplateType;
    shouldShowJudgeSection: boolean;
  };
};

export type SkillToLuckComponent = ComponentGeneric<ComponentType.SkillToLuck> & {
  items: JudgingComponent["data"]["preferSkillToLuckPrizes"];
  title: string;
  subtitle: string;
  assets: React.ImgHTMLAttributes<HTMLImageElement>[];
};

export type JudgingMainComponent = ComponentGeneric<ComponentType.JudgingMain> & { data: JudgingComponent["data"] };

export type JudgingComponent = ComponentGeneric<ComponentType.Judging> & {
  data: {
    judgesMediaUrl: string;
    judgesMediaType: string;
    confirmationLetterUrl: string;
    mediaThumbnailUrl: string;
    widgetCompetitionId: number;
    preferSkillToLuckPrizes: JudgingTicketPreferSkillToLuckPrizes[];
    shouldShowJudgeSection: boolean;
  };
};

export type JudgingTicket = {
  ProductId: number;
  Sku: string;
  ModelName: string;
  Description: string;
  CurrencySymbol: string;
  CurrencyCode: string;
  Price: number;
  PriceGbp: number;
  FormattedPrice: string;
  MultiBuy5FormattedPrice: string;
  MultiBuy10FormattedPrice: string;
};

export type JudgingTicketPreferSkillToLuckPrizes = {
  MphTime: string;
  Power: number;
  TopSpeed: number;
  Torque: string;
  ContentNodeId: number;
  Url: string;
  SortOrder: number;
  SortGroup: number;
  Model: string;
  Make: string;
  MinimumLoyaltyClubLevelId: number;
  Styles: string[];
  ThumbnailImage: string;
  BaseTicket: JudgingTicket;
  SaleTicket: JudgingTicket | null;
  OverrideDiscountValue: number | null;
  CashAlternative: string;
  CashAlternativeAmountGbp: number;
  HasCashAlternative: boolean;
  IsFeatured: boolean;
  QuantityAvailable: number;
  ActivePrice: number;
  ActiveTicket: JudgingTicket;
  StyleCssClasses: string;
  UserCanPurchase: boolean;
  LoyaltyClubFreePrize: number[];
  Name: string;
  DocumentType: string;
  ManufacturerLogoImage: string;
  Category: string;
  Subcategory: string;
  DetailsTabText: string | null;
  PrizeAdditionalCashGBP: number | null;
  AdditionalCashText: string;
  RetailPriceGbp: number;
  CompetitionEndDate: string | null;
  CompetitionId: number;
  CompetitionTypeName: string;
  PrizePrefix: string | null;
  FeaturedTileVideo: string | null;
  SwitchMakeAndModel: boolean;
  HideCashAlternative: boolean;
  MaxTicketsPerUser: number;
  OptimizeUrl: string | null;
  PercentOffValue: number;
  SendTrustPilotWhenWon: boolean;
  IsCashPrize: boolean;
  GameCreditAlternativeOverrideGbp: number | null;
  QuickBuyFirstButtonValue: number;
  QuickBuySecondButtonValue: number;
  QuickBuyThirdButtonValue: number;
};

export type TopWinnerItem = {
  id: number;
  userName: string;
  make: string;
  model: string;
  prizeThumbnailImageUrl: string;
  prizeUrl: string;
};

export type TopWinnersComponent = ComponentGeneric<ComponentType.TopWinners> & {
  label: string;
  competitionId: number;
  instantWinWinnersUrl: string;
};

export type AllWinnersPrizesComponent = ComponentGeneric<ComponentType.AllWinnersPrizes> & {
  competitionId: number;
  instantWinWinnersUrl: string;
};

export type ExploreCompetitionsComponent = ComponentGeneric<ComponentType.ExploreCompetitions> & {
  competitionType?: string;
  competitionIds?: number[];
  title?: string;
  breakpoints?: SwiperOptions["breakpoints"];
};

export type PrizeMediaComponent = ComponentGeneric<ComponentType.PrizeMedia> & {
  data: {
    mediaUrls: string[];
    title: string;
  };
};

export type HouseSliderComponent = ComponentGeneric<ComponentType.HouseSlider> & {
  data: PrizeMediaComponent["data"];
};

export type ApiComponents = ComponentTypeMap[keyof ComponentTypeMap];

type LazyComponent = React.LazyExoticComponent<React.ComponentType<any>>;

export type GetPageComponentsResponse = {
  PageComponents: ApiComponents[];
  template: TemplateType;
};

export type ComponentTypeMap = {
  [ComponentType.Header]: WinnersHeaderComponent;
  [ComponentType.SectionTitle]: SectionTitleComponent;
  [ComponentType.Quote]: QuoteComponent;
  [ComponentType.Video]: VideoComponent;
  [ComponentType.CompetitionCard]: CompetitionCardComponent;
  [ComponentType.PrizeDetails]: PrizeDetailsComponent;
  [ComponentType.ResultsDate]: ResultsDateComponent;
  [ComponentType.PrizeWon]: PrizeWonComponent;
  [ComponentType.TheStory]: TheStoryComponent;
  [ComponentType.AsideTitle]: AsideTitleComponent;
  [ComponentType.FeelingLuckyTitle]: FeelingLuckyTitleComponent;
  [ComponentType.AsideCompetition]: AsideCompetitionComponent;
  [ComponentType.ExtraPrizesWrapper]: ExtraPrizesWrapperComponent;
  [ComponentType.ExtraPrizes]: ExtraPrizesComponent;
  [ComponentType.ExtraPrizesHeader]: ExtraPrizesHeaderComponent;
  [ComponentType.ExtraPrizesRunnersUp]: ExtraPrizesRunnersUpComponent;
  [ComponentType.ExtraPrizesClosestByCar]: ExtraPrizesClosestByCarComponent;
  [ComponentType.FeaturedFeelingLucky]: FeaturedFeelingLuckyComponent;
  [ComponentType.FeelingLuckyDailySpin]: ComponentGeneric<ComponentType.FeelingLuckyDailySpin>;
  [ComponentType.GamePhoto]: GamePhotoComponent;
  [ComponentType.WinnersAreTitle]: WinnersAreTitleComponent;
  [ComponentType.WinnersRewardScheme]: WinnersRewardSchemeComponent;
  [ComponentType.WinnersNearMisses]: WinnersNearMissesComponent;
  [ComponentType.Chance]: ChanceComponent;
  [ComponentType.Judging]: JudgingComponent;
  [ComponentType.JudgingTitle]: JudgingTitleComponent;
  [ComponentType.JudgingMain]: JudgingMainComponent;
  [ComponentType.JudgingSideInformation]: JudgingSideInformationComponent;
  [ComponentType.SkillToLuck]: SkillToLuckComponent;
  [ComponentType.ExploreCompetitions]: ExploreCompetitionsComponent;
  [ComponentType.TopWinners]: TopWinnersComponent;
  [ComponentType.AllWinnersPrizes]: AllWinnersPrizesComponent;
  [ComponentType.WinnersGamePhoto]: WinnersGamePhotoComponent;
  [ComponentType.PrizeMedia]: PrizeMediaComponent;
  [ComponentType.HouseSlider]: HouseSliderComponent;
  [ComponentType.MultipleWinners]: MultipleWinnersComponent;
  // ... add other mappings as needed ...
};

export type SliceProps<T extends ComponentType, R extends ComponentGeneric<T>> = Omit<R, "type">;

export function createComponent(
  type: ComponentType.SectionTitle,
  id: string,
  data: Omit<SectionTitleComponent, "type">
): SectionTitleComponent;

export function createComponent(
  type: ComponentType.Quote,
  id: string,
  data: Omit<QuoteComponent, "type">
): QuoteComponent;

export function createComponent(
  type: ComponentType.Video,
  id: string,
  data: Omit<VideoComponent, "type">
): VideoComponent;

export function createComponent(
  type: ComponentType.CompetitionCard,
  id: string,
  data: Omit<CompetitionCardComponent, "type">
): CompetitionCardComponent;

export function createComponent(
  type: ComponentType.ResultsDate,
  id: string,
  data: Omit<ResultsDateComponent, "type">
): ResultsDateComponent;

export function createComponent(
  type: ComponentType.PrizeWon,
  id: string,
  data: Omit<PrizeWonComponent, "type">
): PrizeWonComponent;

export function createComponent(
  type: ComponentType.TheStory,
  id: string,
  data: Omit<TheStoryComponent, "type">
): TheStoryComponent;

export function createComponent(
  type: ComponentType.AsideTitle,
  id: string,
  data: Omit<AsideTitleComponent, "type">
): AsideTitleComponent;

export function createComponent(
  type: ComponentType.FeelingLuckyTitle,
  id: string,
  data: Omit<FeelingLuckyTitleComponent, "type">
): FeelingLuckyTitleComponent;

export function createComponent(
  type: ComponentType.AsideCompetition,
  id: string,
  data: Omit<AsideCompetitionComponent, "type">
): AsideCompetitionComponent;

export function createComponent(
  type: ComponentType.ExtraPrizesWrapper,
  id: string,
  data?: Omit<ExtraPrizesWrapperComponent, "type">
): ExtraPrizesWrapperComponent;

export function createComponent(
  type: ComponentType.ExtraPrizesWrapper,
  id: string,
  data?: Omit<ExtraPrizesWrapperComponent, "type">
): ExtraPrizesWrapperComponent;

export function createComponent(
  type: ComponentType.ExtraPrizesHeader,
  id: string,
  data: Omit<ExtraPrizesHeaderComponent, "type">
): ExtraPrizesHeaderComponent;

export function createComponent(
  type: ComponentType.ExtraPrizesRunnersUp,
  id: string,
  data: Omit<ExtraPrizesRunnersUpComponent, "type">
): ExtraPrizesRunnersUpComponent;

export function createComponent(
  type: ComponentType.ExtraPrizesClosestByCar,
  id: string,
  data: Omit<ExtraPrizesClosestByCarComponent, "type">
): ExtraPrizesClosestByCarComponent;

export function createComponent(
  type: ComponentType.FeaturedFeelingLucky,
  id: string,
  data: Omit<FeaturedFeelingLuckyComponent, "type">
): FeaturedFeelingLuckyComponent;

export function createComponent(
  type: ComponentType.FeelingLuckyDailySpin,
  id: string,
  data?: Omit<SmarticoDailySpinComponent, "type">
): SmarticoDailySpinComponent;

export function createComponent(
  type: ComponentType.WinnersAreTitle,
  id: string,
  data?: Omit<WinnersAreTitleComponent, "type">
): WinnersAreTitleComponent;

export function createComponent(
  type: ComponentType.WinnersNearMisses,
  id: string,
  data?: Omit<WinnersNearMissesComponent, "type">
): WinnersNearMissesComponent;

export function createComponent(
  type: ComponentType.WinnersRewardScheme,
  id: string,
  data?: Omit<WinnersRewardSchemeComponent, "type">
): WinnersRewardSchemeComponent;

export function createComponent(
  type: ComponentType.WinnersGamePhoto,
  id: string,
  data?: Omit<WinnersGamePhotoComponent, "type">
): WinnersGamePhotoComponent;

export function createComponent(
  type: ComponentType.Chance,
  id: string,
  data?: Omit<ChanceComponent, "type">
): ChanceComponent;

export function createComponent(
  type: ComponentType.JudgingTitle,
  id: string,
  data?: Omit<JudgingTitleComponent, "type">
): JudgingTitleComponent;

export function createComponent(
  type: ComponentType.JudgingMain,
  id: string,
  data?: Omit<JudgingMainComponent, "type">
): JudgingMainComponent;

export function createComponent(
  type: ComponentType.JudgingSideInformation,
  id: string,
  data?: Omit<JudgingSideInformationComponent, "type">
): JudgingSideInformationComponent;

export function createComponent(
  type: ComponentType.SkillToLuck,
  id: string,
  data?: Omit<SkillToLuckComponent, "type">
): SkillToLuckComponent;

export function createComponent(
  type: ComponentType.TopWinners,
  id: string,
  data: Omit<TopWinnersComponent, "type">
): TopWinnersComponent;

export function createComponent(
  type: ComponentType.AllWinnersPrizes,
  id: string,
  data: Omit<AllWinnersPrizesComponent, "type">
): AllWinnersPrizesComponent;

export function createComponent(
  type: ComponentType.ExploreCompetitions,
  id: string,
  data?: Omit<ExploreCompetitionsComponent, "type">
): ExploreCompetitionsComponent;

export function createComponent(
  type: ComponentType.HouseSlider,
  id: string,
  data?: Omit<HouseSliderComponent, "type">
): HouseSliderComponent;

export function createComponent(
  type: ComponentType.MultipleWinners,
  id: string,
  data?: Omit<MultipleWinnersComponent, "type">
): MultipleWinnersComponent;

// Implementation - handles all cases
export function createComponent(type: ComponentType, id: string, data?: any): ApiComponents {
  return {
    id,
    type,
    ...(data || {}),
  };
}

export const COMPONENTS_MAPPER: Partial<Record<ComponentType, LazyComponent>> = {
  [ComponentType.Header]: lazy(() => import("./components/WinnersHeader")),
  [ComponentType.WinnersDreamCar]: lazy(() => import("./components/WinnersDreamCar")),
  [ComponentType.FeelingLucky]: lazy(() => import("./components/FeelingLucky")),
  [ComponentType.Chance]: lazy(() => import("./components/Chance")),
  [ComponentType.Judges]: lazy(() => import("./components/Judges")),
  [ComponentType.SkillToLuck]: lazy(() => import("./components/SkillToLuck")),
  [ComponentType.ExploreCompetitions]: lazy(() => import("./components/ExploreCompetitions")),
  [ComponentType.WinnerIs]: lazy(() => import("./components/WinnerIs")),
  [ComponentType.HouseSlider]: lazy(() => import("./components/HouseSlider")),
  [ComponentType.AllWinnersPrizes]: lazy(() => import("./components/AllWinnersPrizes")),
  [ComponentType.ResultsDate]: lazy(() => import("./components/ResultsDate")),
  [ComponentType.PrizeWon]: lazy(() => import("./components/PrizeWon")),
  [ComponentType.TheStory]: lazy(() => import("./components/TheStory")),
  [ComponentType.PickOfTheWeekList]: lazy(() => import("./components/PickOfTheWeekList")),
  [ComponentType.TopWinners]: lazy(() => import("./easy-components/TopWinners")),

  [ComponentType.SectionTitle]: lazy(() => import("./easy-components/SectionTitle")),
  [ComponentType.Quote]: lazy(() => import("./easy-components/Quote")),
  [ComponentType.Video]: lazy(() => import("./easy-components/Video")),
  [ComponentType.CompetitionCard]: lazy(() => import("./easy-components/CompetitionCard")),
  [ComponentType.AsideTitle]: lazy(() => import("./easy-components/AsideTitle")),
  [ComponentType.FeelingLuckyTitle]: lazy(() => import("./easy-components/FeelingLuckyTitle")),
  [ComponentType.AsideCompetition]: lazy(() => import("./easy-components/AsideCompetition")),
  [ComponentType.ExtraPrizesHeader]: lazy(() => import("./easy-components/ExtraPrizesHeader")),
  [ComponentType.ExtraPrizesWrapper]: lazy(() => import("./easy-components/ExtraPrizesDCWrapper")),
  [ComponentType.ExtraPrizesRunnersUp]: lazy(() => import("./easy-components/ExtraPrizesRunnersUp")),
  [ComponentType.ExtraPrizesClosestByCar]: lazy(() => import("./easy-components/ExtraPrizesClosestByCar")),

  [ComponentType.FeaturedFeelingLucky]: lazy(() => import("./easy-components/FeaturedFeelingLucky")),
  [ComponentType.FeelingLuckyDailySpin]: lazy(() => import("./easy-components/SmarticoDailySpinComponent")),
  [ComponentType.WinnersAreTitle]: lazy(() => import("./easy-components/WinnersAreTitle")),
  [ComponentType.WinnersNearMisses]: lazy(() => import("./easy-components/WinnersNearMisses")),
  [ComponentType.WinnersRewardScheme]: lazy(() => import("./easy-components/WinnersRewardScheme")),
  [ComponentType.WinnersGamePhoto]: lazy(() => import("./easy-components/WinnersGamePhoto")),

  [ComponentType.JudgingTitle]: lazy(() => import("./easy-components/JudgingTitle")),
  [ComponentType.JudgingMain]: lazy(() => import("./easy-components/JudgingMain")),
  [ComponentType.JudgingSideInformation]: lazy(() => import("./easy-components/JudgesSideInformation")),
  [ComponentType.MultipleWinners]: lazy(() => import("./components/MultipleWinners")),
};

export const getComponent = (component: ApiComponents): React.ReactNode => {
  const Component = COMPONENTS_MAPPER[component.type];
  if (!Component) return null;

  return <Component sliceData={component} />;
};
